import { FunctionComponent, useCallback } from 'react';
import { ChevronDownIcon } from '@heroicons/react/solid';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import {
  Link, RichText, RichTextBlock,
} from 'prismic-reactjs';
import { prismicLinkResolver } from 'utils/common';
import { SubmitHandler, useForm } from 'react-hook-form';
import classNames from 'classnames';
import { Document } from '@prismicio/client/types/documents';
import useLocale from 'hooks/useLocale';
import { useTrackingPayload } from 'hooks/useTrackPayload';
import set from 'lodash.set';
import cloneDeep from 'lodash.clonedeep';
import { TrackEvents } from 'utils/constants';
import { useAnalytics } from 'hooks/useAnalytics';
import { hasLink } from 'utils/prismic';
import LinkWithQuery from './LinkWithQuery';
import FormInput from './FormInput';

const socialIcons = [
  {
    name: 'Facebook',
    icon: (props) => (
      <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
        <path
          fillRule="evenodd"
          // eslint-disable-next-line max-len
          d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
          clipRule="evenodd"
        />
      </svg>
    ),
  },
  {
    name: 'Instagram',
    icon: (props) => (
      <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
        <path
          fillRule="evenodd"
          // eslint-disable-next-line max-len
          d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
          clipRule="evenodd"
        />
      </svg>
    ),
  },
  {
    name: 'Twitter',
    icon: (props) => (
      <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
        {/* eslint-disable-next-line max-len */}
        <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
      </svg>
    ),
  },
  {
    name: 'LinkedIn',
    icon: (props) => (
      <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
        <path
          fillRule="evenodd"
          // eslint-disable-next-line max-len
          d="M17.833 2H6.167A4.167 4.167 0 002 6.167v11.666C2 20.134 3.866 22 6.167 22h11.666A4.167 4.167 0 0022 17.833V6.167C22 3.866 20.135 2 17.833 2zM8.667 17.833h-2.5V8.667h2.5v9.166zM7.417 7.61a1.465 1.465 0 01-1.459-1.47c0-.812.654-1.47 1.459-1.47s1.458.658 1.458 1.47c0 .812-.652 1.47-1.458 1.47zm11.25 10.223h-2.5v-4.67c0-2.806-3.334-2.594-3.334 0v4.67h-2.5V8.667h2.5v1.47c1.164-2.155 5.834-2.314 5.834 2.064v5.632z"
          clipRule="evenodd"
        />
      </svg>
    ),
  },
  {
    name: 'YouTube',
    icon: (props) => (
      <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
        <path
          fillRule="evenodd"
          // eslint-disable-next-line max-len
          d="M16.23 7.102c-2.002-.136-6.462-.135-8.461 0-2.165.148-2.419 1.456-2.436 4.898.017 3.436.27 4.75 2.437 4.898 1.999.135 6.459.136 8.461 0 2.165-.148 2.42-1.457 2.437-4.898-.018-3.436-.271-4.75-2.438-4.898zm-6.23 7.12v-4.444l4.778 2.218-4.778 2.226zm2-12.222c5.514 0 10 4.486 10 10s-4.486 10-10 10-10-4.486-10-10 4.486-10 10-10zm0-2c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12z"
          clipRule="evenodd"
        />
      </svg>
    ),
  },
];

interface ItemProps {
  title: RichTextBlock[],
  links: {label: string, link: Link}[],
}

const FooterItemLinks: FunctionComponent<ItemProps> = ({ title, links }) => {
  const richTitle = RichText.asText(title);

  return (
    <>
      <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase inline-block">{richTitle}</h3>
      <ul className={classNames('mt-4 space-y-4')}>
        {links?.map(({ label, link }) => (
          <li key={label}>
            {
              hasLink(link) ? (
                <LinkWithQuery
                  href={Link.url(link, prismicLinkResolver)}
                  target={link?.target ?? '_self'}
                  className="text-base text-gray-300 hover:text-white"
                >
                  {label}
                </LinkWithQuery>
              ) : (
                <span className="text-base text-gray-300 hover:text-white hover:cursor-pointer">
                  {label}
                </span>
              )
            }
          </li>
        ))}
      </ul>
    </>
  );
};

type NewsletterForm = {
  email: string;
};

interface Props {
  data: Document['data'];
}

const Footer: FunctionComponent<Props> = ({ data }) => {
  const intl = useIntl();
  const { track, identify } = useAnalytics();
  const router = useRouter();
  const locale = useLocale();
  const {
    register, handleSubmit, setValue, formState,
  } = useForm<NewsletterForm>();
  const trackPayload = useTrackingPayload({});

  const changeLanguage = (e: React.ChangeEvent<HTMLSelectElement>) => {
    router?.push(
      {
        pathname: '/',
        query: '',
      },
      undefined,
      { locale: e.target.value },
    );
  };

  const onSubmit: SubmitHandler<NewsletterForm> = useCallback(({ email }) => {
    const emailNewsletter = email.toLocaleLowerCase();

    const payload = cloneDeep(trackPayload);
    set(payload, 'context.section', 'footer');
    set(payload, 'email', emailNewsletter);
    set(payload, 'fields.email', emailNewsletter);

    identify({ email: emailNewsletter });

    track(TrackEvents.newsletterFormSubmitted, payload, locale, true);
    setValue('email', '');
  }, [trackPayload, locale, identify, setValue, track]);

  return (
    <footer className="bg-white dark:bg-black-1 lg:pb-14" aria-labelledby="footer-heading">
      <h2 id="footer-heading" className="sr-only">
        Footer
      </h2>
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8">
        <div className="xl:grid xl:grid-cols-3 xl:gap-8">
          <div className="grid grid-cols-1 gap-8 xl:col-span-2">
            <div className="md:grid md:grid-cols-3 md:gap-8">
              {data.col1Show ? (
                <div>
                  <FooterItemLinks title={data.col1Title} links={data.col1Links} />
                </div>
              ) : <div />}
              {data.col2Show ? (
                <div className="mt-12 md:mt-0">
                  <FooterItemLinks title={data.col2Title} links={data.col2Links} />
                </div>
              ) : <div />}
              {data.col3Show ? (
                <div>
                  <FooterItemLinks title={data.col3Title} links={data.col3Links} />
                </div>
              ) : <div />}
            </div>
          </div>
          <div className="mt-8 xl:mt-0">
            {data.showNewsletter && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">
                <FormattedMessage defaultMessage="Suscríbete al newsletter" />
              </h3>
              <p className="mt-4 text-sm text-gray-300">
                <FormattedMessage defaultMessage="Las últimas noticias y recursos sobre habilidades digitales." />
              </p>
              <form
                className="mt-4 sm:max-w-md"
                onSubmit={handleSubmit(onSubmit)}
                noValidate
              >
                <div className="sm:flex">
                  <label htmlFor="email" className="sr-only">
                    <FormattedMessage defaultMessage="E-mail" />
                  </label>
                  <FormInput
                    id="email"
                    type="email"
                    hasError={!!formState.errors?.email}
                    register={register('email', {
                      required: true,
                      pattern: /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/i,
                    })}
                    placeholder={intl.formatMessage({
                      defaultMessage: 'Ingresa tu e-mail',
                    })}
                  />
                  <div className="mt-3 sm:mt-0 sm:ml-3 sm:shrink-0">
                    <button type="submit" className="dh-button-sm">
                      <FormattedMessage
                        defaultMessage="Suscribirme"
                        description="footer button"
                      />
                    </button>
                  </div>
                </div>
                {formState.errors?.email && (
                <p className="mt-2 text-sm text-red-500">
                  <FormattedMessage
                    defaultMessage="Uh, algo salió mal! Por favor, revisá tu email e intentá nuevamente."
                  />
                </p>
                )}
                {!formState.errors?.email && formState.isSubmitSuccessful && (
                <p className="mt-2 text-sm text-green-600">
                  <FormattedMessage defaultMessage="Su mail se registró con éxito!" />
                </p>
                )}
              </form>
            </div>

            )}
            {data?.showLangSelector && (
            <div className="mt-6 md:mt-0">
              <h3 className="text-sm font-semibold text-gray-400 tracking-wider uppercase">
                <FormattedMessage
                  defaultMessage="Idioma"
                  description="footer section"
                />
              </h3>
              <form className="mt-4 sm:max-w-xs">
                <fieldset className="w-full">
                  <label htmlFor="language" className="sr-only">
                    <FormattedMessage
                      defaultMessage="Idioma"
                      description="footer section"
                    />
                  </label>
                  <div className="relative">
                    <select
                      id="language"
                      name="language"
                      className={classNames(
                        'appearance-none block w-full bg-none bg-white border border-gray-300 rounded-md py-2 pl-3',
                        'pr-10 text-base sm:text-sm text-gray-900 focus:outline-none',
                        'focus:ring-primary-500 focus:border-primary-500',
                      )}
                      value={locale}
                      onChange={changeLanguage}
                    >
                      <option value="ar">Español (Argentina)</option>
                      <option value="mx">Español (Mexico)</option>
                      <option value="co">Español (Colombia)</option>
                      <option value="br">Português (Brasil)</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 px-2 flex items-center">
                      <ChevronDownIcon
                        className="h-4 w-4 text-gray-400"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                </fieldset>
              </form>
            </div>
            )}
          </div>
        </div>
        <div className="mt-8 border-t border-gray-700 pt-8 md:flex md:items-center md:justify-between">
          <div className="flex space-x-6 md:order-2">
            {data.socialLinks.map((link) => (
              <LinkWithQuery
                key={link.socialNetwork}
                title={link.socialNetwork}
                href={Link.url(link.url, prismicLinkResolver)}
                target={link?.url?.target ?? '_self'}
                className="text-gray-400 hover:text-gray-300"
              >
                <span className="sr-only">{link.socialNetwork}</span>
                {socialIcons.map(
                  (icon) => icon.name === link.socialNetwork && (
                    <icon.icon
                      key={icon.name}
                      className="h-6 w-6"
                      aria-hidden="true"
                    />
                  ),
                )}
              </LinkWithQuery>
            ))}
          </div>
          <p className="mt-8 text-base text-gray-400 md:mt-0 md:order-1">
            {`© ${new Date().getFullYear()} Digital House`}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
